import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import Icon from './Icon';
import { fmtTime } from './ui';
import './Mobile.css';

const HOLD_MS = 7000;
const SWIPE_THRESHOLD = 80;

// ─── Top Bar ─────────────────────────────────────────────────────────────────
const MobileTopBar = ({ aktiveCount }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 20px 14px', flexShrink: 0,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{
        width: 32, height: 32, borderRadius: 9,
        background: 'linear-gradient(135deg, var(--red), var(--red-deep))',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon name="local_fire_department" size={20} color="#fff" />
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', lineHeight: 1.1 }}>Alarm-App</div>
        <div style={{
          fontSize: 10, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)',
          letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>JF Ka/Kö</div>
      </div>
    </div>
    <div style={{
      padding: '5px 10px', borderRadius: 999,
      background: aktiveCount > 0 ? 'var(--red)' : 'rgba(255,255,255,0.06)',
      color: '#fff', fontSize: 11, fontWeight: 700,
      fontFamily: 'var(--font-mono)', letterSpacing: '0.06em',
      display: 'flex', alignItems: 'center', gap: 6,
    }}>
      {aktiveCount > 0 && (
        <span style={{
          width: 6, height: 6, borderRadius: '50%', background: '#fff',
          animation: 'pulse-dot 1s ease-in-out infinite',
        }} />
      )}
      {aktiveCount > 0 ? `${aktiveCount} AKTIV` : 'BEREIT'}
    </div>
  </div>
);

// ─── Einsatz Card ─────────────────────────────────────────────────────────────
const MobileEinsatzCard = ({
  einsatz, fahrzeuge, dragX = 0,
  onDragStart, onDragMove, onDragEnd,
  isFront, isBehind,
}) => {
  const { status } = einsatz;
  const statusColor = status === 'aktiv' ? 'var(--red)' : status === 'erstellt' ? 'var(--amber)' : 'var(--text-dim)';
  const statusLabel = status === 'aktiv' ? 'AKTIV' : status === 'erstellt' ? 'WARTET' : 'BEENDET';

  const rotation = dragX * 0.05;
  const frontOpacity = 1 - Math.min(Math.abs(dragX) / 300, 0.3);
  const scale = isFront ? 1 : isBehind === 1 ? 0.94 : 0.88;
  const translateY = isBehind ? isBehind * 10 : 0;
  const cardOpacity = isBehind > 1 ? 0 : 1;

  const showHint = isFront && Math.abs(dragX) > 20;
  const hintRight = dragX > 0;

  return (
    <div
      style={{
        position: 'absolute', inset: 0,
        transform: isFront
          ? `translateX(${dragX}px) rotate(${rotation}deg)`
          : `translateY(${translateY}px) scale(${scale})`,
        opacity: isFront ? frontOpacity : cardOpacity,
        transition: isFront && dragX === 0
          ? 'transform 0.3s cubic-bezier(0.2,0.7,0.3,1)'
          : 'none',
        touchAction: 'none',
        cursor: isFront ? 'grab' : 'default',
        zIndex: isFront ? 3 : isBehind === 1 ? 2 : 1,
      }}
      onPointerDown={isFront ? onDragStart : undefined}
      onPointerMove={isFront ? onDragMove : undefined}
      onPointerUp={isFront ? onDragEnd : undefined}
      onPointerCancel={isFront ? onDragEnd : undefined}
    >
      <div className={`mobile-card-inner${status === 'aktiv' ? ' aktiv' : ''}`}>

        {/* Status + Zeit */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 11px', borderRadius: 999,
            background: status === 'aktiv' ? statusColor : 'rgba(255,255,255,0.06)',
            color: status === 'aktiv' ? '#fff' : statusColor,
            border: status !== 'aktiv' ? `1px solid ${statusColor}` : 'none',
            fontSize: 11, fontWeight: 700,
            fontFamily: 'var(--font-mono)', letterSpacing: '0.1em',
          }}>
            {status === 'aktiv' && (
              <span style={{
                width: 6, height: 6, borderRadius: '50%', background: '#fff',
                animation: 'pulse-dot 1s ease-in-out infinite',
              }} />
            )}
            {statusLabel}
          </span>
          <span style={{
            fontSize: 12, color: 'var(--text-dim)',
            fontFamily: 'var(--font-mono)', letterSpacing: '0.04em',
          }}>
            {fmtTime(einsatz.timestamp)}
          </span>
        </div>

        {/* Stichwort */}
        <div className="mobile-stichwort">{einsatz.stichwort}</div>

        {/* Adresse */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
          <Icon name="location_on" size={18} color="var(--amber)" />
          <div style={{ fontSize: 15, color: 'var(--text)', fontWeight: 500, lineHeight: 1.35, flex: 1 }}>
            {einsatz.adresse}
          </div>
        </div>

        {/* Beschreibung */}
        {einsatz.beschreibung && (
          <div style={{
            padding: '12px 14px', borderRadius: 12,
            background: 'rgba(255,255,255,0.04)',
            borderLeft: '3px solid var(--amber)',
            fontSize: 13, color: 'var(--text-mute)', lineHeight: 1.5, marginBottom: 14,
            display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {einsatz.beschreibung}
          </div>
        )}

        {/* Mitteiler */}
        {einsatz.mitteiler && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <Icon name="call" size={16} color="var(--text-mute)" />
            <span style={{ fontSize: 13, color: 'var(--text-mute)', fontWeight: 500 }}>
              {einsatz.mitteiler}
              {einsatz.telefon && <span style={{ color: 'var(--text-dim)' }}> · {einsatz.telefon}</span>}
            </span>
          </div>
        )}

        {/* Fahrzeuge */}
        <div style={{ marginTop: 'auto' }}>
          <div style={{
            fontSize: 10, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)',
            letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8,
          }}>
            Zu alarmieren · {einsatz.alarmierteFahrzeuge.length}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {einsatz.alarmierteFahrzeuge.map((fid) => {
              const f = fahrzeuge.find((x) => x.id === fid);
              if (!f) return null;
              const alarmiert = f.status === 'alarmiert';
              return (
                <span key={fid} style={{
                  padding: '4px 9px', borderRadius: 7,
                  background: alarmiert ? 'var(--red)' : 'rgba(255,255,255,0.06)',
                  color: alarmiert ? '#fff' : 'var(--text)',
                  fontSize: 12, fontWeight: 600,
                  fontFamily: 'var(--font-mono)', letterSpacing: '0.02em',
                }}>
                  {f.name}
                </span>
              );
            })}
          </div>
        </div>
      </div>

      {/* Swipe-Indikator */}
      {showHint && (
        <div
          className="mobile-swipe-hint"
          style={{
            [hintRight ? 'right' : 'left']: 24,
            transform: `translateY(-50%) rotate(${hintRight ? -8 : 8}deg)`,
            color: hintRight ? 'var(--green)' : 'var(--amber)',
            border: `2px solid ${hintRight ? 'var(--green)' : 'var(--amber)'}`,
            opacity: Math.min(Math.abs(dragX) / SWIPE_THRESHOLD, 1),
          }}
        >
          {hintRight ? 'Weiter' : 'Zurück'}
        </div>
      )}
    </div>
  );
};

// ─── Hold-to-Alarm Button ─────────────────────────────────────────────────────
const HoldToAlarm = ({ einsatz, onFire }) => {
  const [progress, setProgress] = useState(0);
  const [holding, setHolding] = useState(false);
  const startRef = useRef(0);
  const rafRef = useRef(0);
  const firedRef = useRef(false);

  const isActive = einsatz?.status === 'aktiv';
  const isBeendet = einsatz?.status === 'beendet';
  const disabled = !einsatz || isActive;

  const tick = useCallback(() => {
    const p = Math.min((Date.now() - startRef.current) / HOLD_MS, 1);
    setProgress(p);
    if (p >= 1 && !firedRef.current) {
      firedRef.current = true;
      cancelAnimationFrame(rafRef.current);
      setHolding(false);
      setProgress(0);
      if (onFire && einsatz) onFire(einsatz);
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [einsatz, onFire]);

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    firedRef.current = false;
    startRef.current = Date.now();
    setHolding(true);
    rafRef.current = requestAnimationFrame(tick);
  };

  const stop = () => {
    cancelAnimationFrame(rafRef.current);
    setHolding(false);
    setProgress(0);
  };

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const RING_R = 36;
  const CIRC = 2 * Math.PI * RING_R;

  const holdSec = Math.round(HOLD_MS / 1000);

  return (
    <div className="mobile-hold-wrap">
      <button
        className={`mobile-hold-btn ${isActive ? 'alarm' : 'ready'}${holding ? ' holding' : ''}`}
        onPointerDown={start}
        onPointerUp={stop}
        onPointerLeave={stop}
        onPointerCancel={stop}
        disabled={disabled}
      >
        {/* Fortschrittsring */}
        <div style={{ position: 'relative', width: 84, height: 84, flexShrink: 0, marginLeft: -16 }}>
          <svg width="84" height="84" style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}>
            <circle cx="42" cy="42" r={RING_R} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="4" />
            <circle
              cx="42" cy="42" r={RING_R} fill="none"
              stroke="#fff" strokeWidth="4" strokeLinecap="round"
              strokeDasharray={CIRC}
              strokeDashoffset={CIRC * (1 - progress)}
              style={{ transition: holding ? 'none' : 'stroke-dashoffset 0.25s' }}
            />
          </svg>
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon
              name={isActive ? 'check_circle' : 'notifications_active'}
              size={32} color="#fff"
            />
          </div>
        </div>

        {/* Text */}
        <div style={{ textAlign: 'left', flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 10, opacity: 0.8, fontFamily: 'var(--font-mono)',
            letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4,
          }}>
            {isActive ? 'Alarmiert' : `Halten zum Alarmieren`}
          </div>
          <div style={{
            fontSize: 17, fontWeight: 700, letterSpacing: '-0.005em',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {isActive
              ? 'Alarm läuft'
              : isBeendet
                ? `Erneut alarmieren · ${holdSec} Sek halten`
                : einsatz
                  ? `Alarm auslösen · ${holdSec} Sek halten`
                  : 'Kein Einsatz ausgewählt'}
          </div>
        </div>

        {/* Füllbalken */}
        {!disabled && (
          <div style={{
            position: 'absolute', inset: 0, borderRadius: 'inherit',
            background: 'rgba(255,255,255,0.18)',
            transform: `translateX(${-100 + progress * 100}%)`,
            pointerEvents: 'none',
          }} />
        )}
      </button>
    </div>
  );
};

// ─── Navigations-Punkte ───────────────────────────────────────────────────────
const CardDots = ({ count, active, onSelect }) => (
  <div className="mobile-dots">
    {Array.from({ length: count }).map((_, i) => (
      <button
        key={i}
        onClick={() => onSelect(i)}
        className={`mobile-dot${i === active ? ' active' : ''}`}
        style={{ width: i === active ? 22 : 6 }}
      />
    ))}
  </div>
);

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ children, onDone }) => {
  useEffect(() => {
    const id = setTimeout(onDone, 2400);
    return () => clearTimeout(id);
  }, [onDone]);
  return (
    <div className="mobile-toast">
      <Icon name="check_circle" size={20} color="var(--green)" />
      {children}
    </div>
  );
};

// ─── MobileApp (Top-Level) ────────────────────────────────────────────────────
const MobileApp = () => {
  const [fahrzeuge, setFahrzeuge] = useState([]);
  const [einsaetze, setEinsaetze] = useState([]);
  const [index, setIndex] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [toast, setToast] = useState(null);
  const dragRef = useRef({ startX: 0, dragging: false });

  useEffect(() => {
    const load = async () => {
      try {
        const [fzRes, esRes] = await Promise.all([
          axios.get('/api/fahrzeuge'),
          axios.get('/api/einsaetze'),
        ]);
        setFahrzeuge(fzRes.data);
        setEinsaetze(esRes.data);
      } catch (e) {
        console.error('Mobile: Ladefehler', e);
      }
    };
    load();

    const socket = io();
    socket.on('fahrzeugeUpdated', (u) => setFahrzeuge(u));
    socket.on('einsatzErstellt', (e) =>
      setEinsaetze((prev) => (prev.some((x) => x.id === e.id) ? prev : [...prev, e]))
    );
    socket.on('einsatzAusgeloest', (e) =>
      setEinsaetze((prev) => prev.map((x) => (x.id === e.id ? e : x)))
    );
    socket.on('einsatzBeendet', (e) =>
      setEinsaetze((prev) => prev.map((x) => (x.id === e.id ? e : x)))
    );
    socket.on('einsatzAktualisiert', (e) =>
      setEinsaetze((prev) => prev.map((x) => (x.id === e.id ? e : x)))
    );
    socket.on('einsatzGeloescht', (id) =>
      setEinsaetze((prev) => prev.filter((x) => x.id !== id))
    );
    socket.on('einsaetzeReset', (all) => setEinsaetze(all));

    return () => socket.close();
  }, []);

  const sorted = useMemo(() => {
    const order = { erstellt: 0, aktiv: 1, beendet: 2 };
    return [...einsaetze].sort(
      (a, b) =>
        (order[a.status] ?? 3) - (order[b.status] ?? 3) ||
        new Date(b.timestamp) - new Date(a.timestamp)
    );
  }, [einsaetze]);

  // Index in bounds halten wenn Einsätze wegfallen
  useEffect(() => {
    if (sorted.length > 0 && index >= sorted.length) {
      setIndex(sorted.length - 1);
    }
  }, [sorted.length, index]);

  const animateTo = useCallback((target, then) => {
    const start = performance.now();
    const dur = 220;
    const startX = dragX;
    const step = (t) => {
      const k = Math.min((t - start) / dur, 1);
      const ease = 1 - Math.pow(1 - k, 3);
      setDragX(startX + (target - startX) * ease);
      if (k < 1) {
        requestAnimationFrame(step);
      } else {
        setDragX(0);
        then && then();
      }
    };
    requestAnimationFrame(step);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragX]);

  const onDragStart = (e) => {
    dragRef.current = { startX: e.clientX, dragging: true };
    e.currentTarget.setPointerCapture && e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onDragMove = (e) => {
    if (!dragRef.current.dragging) return;
    setDragX(e.clientX - dragRef.current.startX);
  };

  const onDragEnd = () => {
    if (!dragRef.current.dragging) return;
    dragRef.current.dragging = false;
    if (dragX > SWIPE_THRESHOLD && index < sorted.length - 1) {
      animateTo(400, () => setIndex((i) => Math.min(i + 1, sorted.length - 1)));
    } else if (dragX < -SWIPE_THRESHOLD && index > 0) {
      animateTo(-400, () => setIndex((i) => Math.max(i - 1, 0)));
    } else {
      animateTo(0);
    }
  };

  const fireAlarm = async (einsatz) => {
    try {
      if (einsatz.status === 'erstellt') {
        await axios.post(`/api/einsaetze/${einsatz.id}/ausloesen`);
        setToast(`„${einsatz.stichwort}" alarmiert`);
      } else if (einsatz.status === 'beendet') {
        await axios.post(`/api/einsaetze/${einsatz.id}/erneut-ausloesen`);
        setToast(`„${einsatz.stichwort}" erneut alarmiert`);
      }
    } catch (e) {
      console.error('Mobile: Alarm-Fehler', e);
      setToast('Fehler beim Alarmieren');
    }
  };

  const aktiveCount = einsaetze.filter((e) => e.status === 'aktiv').length;
  const current = sorted[index];

  return (
    <div className="mobile-root">
      <MobileTopBar aktiveCount={aktiveCount} />

      {/* Sub-Header */}
      <div style={{ padding: '0 20px 10px', flexShrink: 0 }}>
        <div style={{
          fontSize: 11, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)',
          letterSpacing: '0.1em', textTransform: 'uppercase',
        }}>
          Einsatz {sorted.length === 0 ? 0 : index + 1} von {sorted.length}
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', marginTop: 4, letterSpacing: '-0.01em' }}>
          Aus der Ferne alarmieren
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-mute)', marginTop: 2 }}>
          Wische zwischen den Einsätzen · Halte den Knopf zum Auslösen.
        </div>
      </div>

      {/* Card-Stack */}
      <div className="mobile-card-stack">
        {sorted.length === 0 ? (
          <div className="mobile-empty">
            <Icon name="inbox" size={48} color="var(--text-dim)" />
            <div style={{ fontSize: 16, color: 'var(--text)', fontWeight: 600, marginTop: 16 }}>
              Keine Einsätze
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-mute)', marginTop: 6 }}>
              Erstelle einen Einsatz im Dashboard, um ihn hier auszulösen.
            </div>
          </div>
        ) : (
          sorted.map((e, i) => {
            const offset = i - index;
            if (offset < 0 || offset > 2) return null;
            return (
              <MobileEinsatzCard
                key={e.id}
                einsatz={e}
                fahrzeuge={fahrzeuge}
                dragX={offset === 0 ? dragX : 0}
                isFront={offset === 0}
                isBehind={offset > 0 ? offset : undefined}
                onDragStart={onDragStart}
                onDragMove={onDragMove}
                onDragEnd={onDragEnd}
              />
            );
          })
        )}
        {toast && (
          <Toast onDone={() => setToast(null)}>{toast}</Toast>
        )}
      </div>

      {/* Dots */}
      {sorted.length > 1 && (
        <CardDots count={sorted.length} active={index} onSelect={setIndex} />
      )}

      {/* Hold-Button */}
      <HoldToAlarm einsatz={current} onFire={fireAlarm} />
    </div>
  );
};

export default MobileApp;
